import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGridPro, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid-pro";
import { orderBy } from "lodash-es";
import { useMemo, type FC } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { notEmpty } from "../../../../utils/not-empty";
import { ChannelIcon } from "../../../channels/components/channel-icon";
import type { ChannelName } from "../../../channels/types";
import { prettyChannelName } from "../../../channels/utils";
import type { GetPersonsInSegmentJsonPerson, Segment } from "./types";
import { usePersonsInSegmentJson } from "./moment-segment-preview/hooks/use-persons-in-segment-json";

interface SegmentGroupProps {
  segment: Segment["segment_json"] | Segment["segment_json"]["segments"][number];
  channels: ChannelName[];
  type: "full" | "group";
}

export const SegmentPreview: FC<SegmentGroupProps> = ({ segment, channels, type }) => {
  const { t } = useTranslation();

  const dialog = useDialog<ChannelName>();

  const { data, error, loading } = usePersonsInSegmentJson(
    segment.type === "segment" && segment.segments.length === 0 ? null : { segment_json: segment },
  );

  const orderedChannels = useMemo(
    () =>
      orderBy(channels, (c) => {
        switch (c) {
          case "work_email":
            return 1;
          case "slack":
            return 2;
          case "teams":
            return 3;
          case "twilio":
            return 4;
          case "personal_email":
            return 5;
          case null:
            return 6;
        }
      })
        .map((c) => {
          if (!c) return null;

          return {
            name: c,
            persons: data?.filter((p) => (p.channels as Record<string, string>)[c]) ?? [],
          };
        })
        .filter(notEmpty),
    [channels, data],
  );

  if (error) {
    console.error("Error loading persons for segment:", error);
    return (
      <Alert severity="error">
        {type === "group"
          ? t("Error loading people in group")
          : t("Error loading people in segment")}
      </Alert>
    );
  }

  if (loading)
    return <Skeleton variant="rectangular" width={"100%"} height={40} sx={{ borderRadius: 1 }} />;

  return (
    <>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="body2">
          {type === "group" && (
            <Trans i18nKey="thereAreCountPeopleGroup" count={data?.length ?? 0}>
              <span
                data-analytics-id="people-in-segment-count"
                style={{ cursor: "pointer", color: "#5048e5" }}
                onClick={() => dialog.handleOpen(null)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") dialog.handleOpen(null);
                }}
                role="link"
                tabIndex={0}
              >
                placeholder
              </span>
            </Trans>
          )}
          {type === "full" && (
            <Trans i18nKey="thereAreCountPeopleTotal" count={data?.length ?? 0}>
              <span
                data-analytics-id="people-in-segment-count"
                style={{ cursor: "pointer", color: "#5048e5" }}
                onClick={() => dialog.handleOpen(null)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") dialog.handleOpen(null);
                }}
                role="link"
                tabIndex={0}
              >
                placeholder
              </span>
            </Trans>
          )}
        </Typography>

        {orderedChannels.map((channel) => (
          <Stack key={channel.name} direction="row" spacing={1} alignItems="center" sx={{ mr: 2 }}>
            <Button
              variant="text"
              onClick={() => dialog.handleOpen(channel.name)}
              disabled={channel.persons.length === 0}
              startIcon={<ChannelIcon channel={channel.name} fontSize="small" />}
              data-analytics-id={`view-recipients-${channel.name}-button`}
            >
              {channel.persons.length}
            </Button>
          </Stack>
        ))}
      </Stack>
      <Dialog
        open={dialog.open}
        onClose={dialog.handleClose}
        title={t("Edit Segment")}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {t("People in Segment Group")}: {prettyChannelName(dialog.data ?? null)}
        </DialogTitle>
        <DialogContent>
          <SegmentPreviewDialogDataGrid
            channelName={dialog.data ?? null}
            persons={
              dialog.data
                ? (orderedChannels.find((c) => c.name === dialog.data!)?.persons ?? [])
                : (data ?? [])
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={dialog.handleClose} data-analytics-id="close-modal-button">
            {t("Cancel")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const SegmentPreviewDialogDataGrid: FC<{
  channelName: ChannelName;
  persons: GetPersonsInSegmentJsonPerson[];
}> = ({ channelName, persons }) => {
  const { t } = useTranslation();

  const columns: GridColDef<GetPersonsInSegmentJsonPerson>[] = useMemo(
    () => [
      {
        field: "first_name",
        headerName: t("First Name"),
        flex: 1,
      },
      {
        field: "last_name",
        headerName: t("Last Name"),
        flex: 1,
      },
      {
        field: "email",
        headerName: t("Email"),
        flex: 1,
      },
      {
        field: "channels",
        headerName: channelName ? t("Channel Display Name") : t("Channels"),
        flex: 1,
        disableExport: true,
        renderCell: (
          params: GridRenderCellParams<
            GetPersonsInSegmentJsonPerson,
            GetPersonsInSegmentJsonPerson["channels"]
          >,
        ) => {
          if (channelName) return (params.value as Record<string, string>)[channelName] || "";

          return (
            <Stack alignItems="center" height="100%" direction="row">
              {channels.map((channel) => {
                const displayName = (params.value as Record<string, string>)[channel];
                return displayName ? (
                  <Tooltip key={channel} title={displayName}>
                    <span>
                      <ChannelIcon channel={channel as ChannelName} fontSize="small" />
                    </span>
                  </Tooltip>
                ) : null;
              })}
            </Stack>
          );
        },
      },
    ],
    [t, channelName],
  );

  return <DataGridPro rows={persons} columns={columns} pagination showToolbar />;
};

const channels = ["slack", "teams", "work_email", "personal_email", "twilio"] as const;
